/**
 * The query result, and the pivot projection over it.
 *
 * The server returns a FLAT result — one row per group-by combination — because
 * rows and columns are one `GROUP BY` and the split into two axes is
 * presentation. `pivot()` below performs that projection on the client.
 */
import { findMeasure, isAdditive, type Measure, type ReportModel } from './model';
import type { AxisSort } from './plan';
import { isLevelSort, type ReportSpec, type ShowAsMode, type SortSpec, showAsFor } from './spec';

/**
 * How a level sort compares two names, and two keys. See `orderTuples`.
 *
 * Built once rather than per comparison, and in one fixed locale, so the order
 * does not depend on where it runs — the browser's language, or the API's Excel
 * export. Keys are also `numeric`, so a key of digits (a QuickBooks id) runs 2
 * before 10; ISO dates and `YYYY-MM` are unaffected. Names are not: a name with
 * a number in it is text, and ordering it as text is what this always did.
 */
const LABEL_COLLATOR = new Intl.Collator('en-US');
const KEY_COLLATOR = new Intl.Collator('en-US', { numeric: true });

/**
 * One dimension's value on one row: what to group and filter by, and what to
 * show. Two employees may share a name; they never share a key, which is why
 * the pivot buckets on `key` and prints `label`.
 */
export interface DimValue {
  key: string | null;
  label: string | null;
}

/** One dimension tuple — a coordinate along one axis. */
export type DimTuple = readonly DimValue[];

/** One aggregated row: dimension values keyed by id, measure values by id. */
export interface ResultRow {
  dims: Record<string, DimValue>;
  values: Record<string, number | null>;
}

/** What the UI needs to explain a number without asking the server again. */
export interface ResultMeta {
  /** Measure id → plain-English formula. Feeds the widget footer. */
  formulas: Record<string, string>;
  /**
   * True when this result does not reach the end of the answer.
   *
   * For a PIVOT that is a cap that bit, and there is no way past it — the
   * footer says so. For a LISTING it means only "there are further pages", and
   * {@link ResultMeta.totalRows} says how many rows they hold. NEVER silent
   * either way.
   */
  truncated: boolean;
  /** Rows in THIS result — one page of a listing, or the pivot's groups. */
  rowCount: number;
  /**
   * DETAIL ONLY. Rows the query matched altogether, before the page was taken.
   *
   * Exact, and it comes back WITH the page: `COUNT(*) OVER ()` rides in the
   * same statement, over the same predicates, so the count and the rows can
   * never be two different reads of a moving table. It is what makes paging
   * possible at all — "page 3 of 41" cannot be worked out from a page.
   *
   * Absent for a pivot, which pages nothing.
   */
  totalRows?: number;
  /**
   * DETAIL ONLY. This page begins past the end of the answer.
   *
   * Its own state rather than a reading of the two above, because it is not one
   * of them: `truncated` means rows are AHEAD and invites a next page, and this
   * page has every remaining row BEHIND it. Reached by a saved spec whose report
   * has since shrunk, or by a next page offered on the strength of a full last
   * page when the transport cannot count. The count is unknown here — an empty
   * page carries no row to read it from — which is why `totalRows` is absent
   * rather than 0, and why a reader needs telling which of the two this is.
   */
  pastEnd?: boolean;
  /**
   * DETAIL ONLY. Measure id → its total over the WHOLE answer, not this page.
   *
   * A listing's total row cannot be worked out from what is on screen once the
   * listing pages: adding up page 1 of 25 gives a number 4% of the truth in the
   * place a reader looks for the truth. So it is summed where the rows are, and
   * only for measures that can be added — a measure absent from this record has
   * no meaningful total and its cell stays blank.
   *
   * Present only when the spec asked for a total row.
   */
  totals?: Record<string, number | null>;
  /** Generated SQL. Present only for callers with the disclosure permission. */
  sql?: string;
  /** Measures omitted because the caller lacked their permission. */
  omittedMeasures?: string[];
  /**
   * Relations this report could not use at its grain, by name.
   *
   * Their measures came back NULL by construction — capacity has no customer
   * column, so "this person's capacity for Acme" has no answer. Reported rather
   * than left implicit: an em dash from a category error and an em dash from
   * missing data look identical, and only the server knows which this was.
   */
  unusableSources?: string[];
  /**
   * Dimension ids whose LABEL was not a function of their KEY in this result —
   * one key that came back under two or more names.
   *
   * A pivot cell is identified by keys alone, so two such rows land on one cell
   * and `pivot()` has to merge them. It does, and nothing is lost; but the
   * merge picks ONE of the labels, so the grid shows a number the reader cannot
   * reconcile with the name beside it. That is a model or SQL defect — a
   * dimension whose label can vary within a key must declare `groupLabelSql` —
   * and the point of this field is that it arrives as a sentence on screen
   * rather than as somebody reconciling a spreadsheet months later.
   *
   * Absent, not `[]`, when the invariant held — which is every result the
   * models produce today.
   */
  ambiguousLabels?: string[];
}

export interface QueryResult {
  rows: ResultRow[];
  meta: ResultMeta;
}

/** A materialised pivot: row tuples × column tuples × measures. */
export interface PivotView {
  rowKeys: DimTuple[];
  colKeys: DimTuple[];
  /** Display value, with `showAs` applied. */
  cell(rowKey: DimTuple, colKey: DimTuple, measureId: string): number | null;
  /** Underlying value, before `showAs`. */
  raw(rowKey: DimTuple, colKey: DimTuple, measureId: string): number | null;
  /** Total across the column axis for one row, in DISPLAY space. */
  rowTotal(rowKey: DimTuple, measureId: string): number | null;
  /** Total down the row axis for one column, in DISPLAY space. */
  colTotal(colKey: DimTuple, measureId: string): number | null;
  grandTotal(measureId: string): number | null;
  /**
   * Did the PROJECTION drop row members that the query returned?
   *
   * A second kind of truncation from `ResultMeta.truncated`, and invisible to
   * it: the cap in `plan.memory.limit` is taken here, after the ranking, so the
   * server has already answered `truncated: false` by the time it bites. It
   * used to be unreachable — the SQL ceiling and the spec's cap were both
   * 5,000, so the axis could never be longer than the cap — and raising the
   * ceiling to 25,000 made it live for every saved spec still carrying the old
   * number. Whatever reads `meta.truncated` has to read this beside it.
   */
  rowsDropped: boolean;
  /**
   * One group's total, for one column. The same aggregate as {@link colTotal}
   * taken over the rows sharing `rowPrefix` rather than over all of them, so
   * it obeys the same honesty rule.
   */
  subtotal(rowPrefix: DimTuple, colKey: DimTuple, measureId: string): number | null;
  /** One group's total across the whole column axis. */
  subtotalAcross(rowPrefix: DimTuple, measureId: string): number | null;
}

/**
 * Tuple → lookup key.
 *
 * NUL is the separator because Postgres text cannot contain one, so no
 * dimension value can forge a boundary. Written as an escape rather than a
 * literal: a raw NUL byte in the source makes git and GitHub classify the file
 * as binary and stop showing its diff.
 */
const SEP = '\u0000';
const keyOf = (parts: readonly (string | null)[]) => parts.map((p) => p ?? '').join(SEP);
const keysOf = (tuple: DimTuple) => keyOf(tuple.map((v) => v.key));

/** Display text for a tuple member — the label, falling back to the key. */
export const dimText = (v: DimValue | undefined): string => v?.label ?? v?.key ?? '';

/** A tuple's join key for React lists and drill coordinates. */
export const tupleKey = (tuple: DimTuple): string => keysOf(tuple);

/**
 * Project a flat result onto two axes.
 *
 * `showAs` is applied here rather than server-side because it is a ratio
 * between cells of THIS result — the row/column/grand denominators only exist
 * once the projection is known.
 *
 * The MODEL is here for the same reason: a total over a ratio has to be rebuilt
 * from that measure's two halves (see {@link ratioParts}), and which halves
 * those are is a fact about the model, not about the result.
 *
 * `memorySort` is the plan's verdict on which axes this projection is
 * responsible for ordering (`plan.memory.sort`). Omit it and both axes are
 * sorted here, which is right for a host whose transport does no ordering;
 * pass it and an axis the query already ordered is left exactly as it arrived,
 * rather than being sorted a second time by a different rule.
 *
 * `memoryLimit` is the other half of the same verdict (`plan.memory.limit`):
 * the row cap, when the projection owns the order and therefore owns the cap.
 * A cap belongs wherever the order was decided, so exactly one of the two ends
 * applies it — and until this existed neither did, so a spec asking for ten
 * rows rendered every group the query returned.
 */
export function pivot(
  model: ReportModel,
  spec: ReportSpec,
  result: QueryResult,
  memorySort?: readonly AxisSort[],
  memoryLimit?: number,
): PivotView {
  const sortFor = (axis: 'rows' | 'cols'): readonly SortSpec[] =>
    memorySort
      ? memorySort.filter((s) => s.axis === axis).map((s) => s.sort)
      : axis === 'rows'
        ? spec.rowSort
        : spec.colSort;

  const ordered = orderTuples(distinctTuples(result.rows, spec.rows), spec.rows, sortFor('rows'));
  // AFTER the ordering, never before — that is the whole reason the cap came
  // here rather than staying in the query.
  const rowKeys = memoryLimit === undefined ? ordered : ordered.slice(0, memoryLimit);
  // REPORTED, not just done. See {@link PivotView.rowsDropped}.
  const rowsDropped = rowKeys.length < ordered.length;
  const colKeys = spec.cols.length
    ? orderTuples(distinctTuples(result.rows, spec.cols), spec.cols, sortFor('cols'))
    : [[] as DimTuple];

  /**
   * One cell, keyed by KEYS ALONE — which means two result rows can land on it.
   *
   * They should not: the compiler groups by the key columns and every label is
   * a function of its key, so one group is one row. But that invariant lives in
   * SQL and in the model, and this is the code that would be wrong if it broke.
   * It did break — a customer renamed mid-period resolved its name AS OF the
   * work date, so one `customer_id` came back under two names — and this map
   * used to `set()` unconditionally, so the second row REPLACED the first.
   * 3,823 hours vanished from a production report's grid, every subtotal and
   * the grand total included, while the server's own totals still counted them.
   * Nothing said so.
   *
   * So colliding rows are MERGED, by the same rule a subtotal uses — because
   * that is what this is: a group of rows reduced to one number.
   *
   *  - ADDITIVE measures (`sum`, `count`) add.
   *  - A RATIO naming two additive halves is rebuilt from the halves' sums,
   *    below. Adding two percentages is the category error `totalOf` exists to
   *    avoid, and it would be silently plausible.
   *  - ANYTHING ELSE (`countDistinct`, an average, a ratio whose halves are not
   *    both selected) cannot be recombined from cells at all, and becomes NULL
   *    — an em dash, the same answer this file gives every other total it
   *    cannot honestly take. Keeping one of the two and calling it the cell
   *    would be a confident wrong number.
   */
  const index = new Map<string, Record<string, number | null>>();
  const merged = new Set<string>();
  for (const row of result.rows) {
    const rk = keyOf(spec.rows.map((d) => row.dims[d]?.key ?? null));
    const ck = keyOf(spec.cols.map((d) => row.dims[d]?.key ?? null));
    const at = `${rk}${SEP}${ck}`;
    const cell = index.get(at);
    if (!cell) {
      index.set(at, { ...row.values });
      continue;
    }
    merged.add(at);
    // The UNION of both rows' measures, not just this row's. A non-additive
    // measure has to be nulled even when this row carries NULL for it or omits
    // it entirely — otherwise the first row's value survives as the cell, which
    // is the confident wrong number the doc comment above rules out.
    for (const mid of new Set([...Object.keys(cell), ...Object.keys(row.values)])) {
      const measure = findMeasure(model, mid);
      if (!measure || !isAdditive(measure)) {
        cell[mid] = null;
        continue;
      }
      // Additive: a NULL or absent contribution is nothing, and nothing added
      // to a sum leaves it alone.
      const value = row.values[mid];
      if (value === null || value === undefined) continue;
      cell[mid] = (cell[mid] ?? 0) + value;
    }
  }
  // Ratios, after every half has finished summing. A second pass rather than a
  // running divide: the numerator and denominator arrive on the same rows as
  // the ratio, so dividing as we go would divide partial sums.
  for (const at of merged) {
    const cell = index.get(at) as Record<string, number | null>;
    for (const mid of Object.keys(cell)) {
      // `ratioHalves`, not `ratioParts`: the latter refuses under a non-raw
      // `showAs` because a TOTAL must not be rebuilt there. Whether this CELL's
      // own raw value can be recovered is a different question, and it can —
      // `showAs` is applied later, by `cell()`, over whatever raw says.
      const parts = ratioHalves(model, mid);
      if (!parts) continue;
      const den = cell[parts.denominator];
      cell[mid] = den ? (cell[parts.numerator] ?? 0) / den : null;
    }
  }

  const raw = (rk: DimTuple, ck: DimTuple, mid: string): number | null =>
    index.get(`${keysOf(rk)}${SEP}${keysOf(ck)}`)?.[mid] ?? null;

  const sumOver = (
    tuples: readonly DimTuple[],
    pick: (t: DimTuple) => number | null,
  ): number | null => {
    let total = 0;
    let seen = false;
    for (const t of tuples) {
      const v = pick(t);
      if (v !== null) {
        total += v;
        seen = true;
      }
    }
    return seen ? total : null;
  };

  /**
   * Memoise a denominator on first use.
   *
   * Every `showAs` mode divides by one of these, and `cell()` is called once
   * per rendered cell — so computed on demand they make a percentage view
   * quadratic in the result size. A 500×10 pivot under "% of total" goes from
   * roughly 25M lookups per render to 5000, on every keystroke in the builder.
   */
  const memo = <K>(compute: (k: K, mid: string) => number | null, id: (k: K) => string) => {
    const cache = new Map<string, number | null>();
    return (k: K, mid: string): number | null => {
      const ck = `${mid}${SEP}${id(k)}`;
      const hit = cache.get(ck);
      if (hit !== undefined || cache.has(ck)) return hit ?? null;
      const value = compute(k, mid);
      cache.set(ck, value);
      return value;
    };
  };

  const rawRowTotal = memo<DimTuple>(
    (rk, mid) => sumOver(colKeys, (ck) => raw(rk, ck, mid)),
    keysOf,
  );
  const rawColTotal = memo<DimTuple>(
    (ck, mid) => sumOver(rowKeys, (rk) => raw(rk, ck, mid)),
    keysOf,
  );
  const rawGrandFor = memo<null>(
    (_, mid) => sumOver(rowKeys, (rk) => rawRowTotal(rk, mid)),
    () => '',
  );
  const rawGrand = (mid: string) => rawGrandFor(null, mid);

  /**
   * A total over one measure, in RAW space, wherever a total is being taken.
   *
   * Raw and not display space, and that is the whole reason these exist beside
   * the four public totals rather than being expressed in terms of them. The
   * public ones sum `cell()`, which has already applied `showAs` — so a ratio
   * rebuilt from them would divide one measure's percentages by another's the
   * moment a reader put Budget Hours on "% of row". `showAs` is a share of a
   * total; it is not a quantity to take a further total of.
   */
  type RawTotal = (measureId: string) => number | null;

  // `rowsUnder` is re-asked for the same prefix once per column per measure
  // across a subtotal band, and each answer is a full scan of the row axis.
  const underCache = new Map<string, DimTuple[]>();

  const cell = (rk: DimTuple, ck: DimTuple, mid: string): number | null => {
    const v = raw(rk, ck, mid);
    if (v === null) return null;
    const mode: ShowAsMode = showAsFor(spec, mid);
    if (mode === 'raw') return v;
    const denom =
      mode === 'pctRow'
        ? rawRowTotal(rk, mid)
        : mode === 'pctCol'
          ? rawColTotal(ck, mid)
          : rawGrand(mid);
    return denom ? v / denom : null;
  };

  /**
   * One total, taken the way THIS measure's total has to be taken.
   *
   * A ratio is rebuilt from its two halves over the same span — the sum of the
   * numerators over the sum of the denominators — because the sum of a column
   * of percentages is not a percentage of anything. Everything else adds up
   * the display values, which is what a total has always been here.
   *
   * The division mirrors the server's `COALESCE(num, 0) / NULLIF(den, 0)`
   * exactly, and that matters more than it looks: a group of one row must total
   * to the very value printed in it, or folding a group would change the
   * number. No denominator is no answer (`—`, not 0); no numerator over a real
   * denominator is 0.
   */
  const totalOf = (mid: string, rawTotal: RawTotal, sumOfCells: () => number | null) => {
    const parts = ratioParts(model, spec, mid);
    if (!parts) return sumOfCells();
    const den = rawTotal(parts.denominator);
    return den ? (rawTotal(parts.numerator) ?? 0) / den : null;
  };

  const rawSubtotal = (prefix: DimTuple, ck: DimTuple, mid: string) =>
    sumOver(rowsUnder(prefix), (rk) => raw(rk, ck, mid));

  return {
    rowKeys,
    colKeys,
    rowsDropped,
    cell,
    raw,
    rowTotal: (rk, mid) =>
      totalOf(
        mid,
        (p) => rawRowTotal(rk, p),
        () => sumOver(colKeys, (ck) => cell(rk, ck, mid)),
      ),
    colTotal: (ck, mid) =>
      totalOf(
        mid,
        (p) => rawColTotal(ck, p),
        () => sumOver(rowKeys, (rk) => cell(rk, ck, mid)),
      ),
    subtotal: (prefix, ck, mid) =>
      totalOf(
        mid,
        (p) => rawSubtotal(prefix, ck, p),
        () => sumOver(rowsUnder(prefix), (rk) => cell(rk, ck, mid)),
      ),
    subtotalAcross: (prefix, mid) =>
      totalOf(
        mid,
        (p) => sumOver(rowsUnder(prefix), (rk) => rawRowTotal(rk, p)),
        () => sumOver(rowsUnder(prefix), (rk) => sumOver(colKeys, (ck) => cell(rk, ck, mid))),
      ),
    grandTotal: (mid) =>
      totalOf(mid, rawGrand, () => {
        const mode = showAsFor(spec, mid);
        if (mode === 'raw') return rawGrand(mid);
        // Under any percentage mode the parts are shares of the same whole, so
        // the corner is 100% by construction. Summing the axis totals instead
        // would report 400% for a four-column "% of column" pivot.
        //
        // Unless there is no whole: with no value anywhere, every cell reads `—`
        // and a corner claiming 100% is a confident wrong number.
        return rawGrand(mid) === null ? null : 1;
      }),
  };

  /** The row tuples sitting under one group. */
  function rowsUnder(prefix: DimTuple): DimTuple[] {
    // NOT `keysOf`. The comparison below is strict, so the cache key has to be
    // exactly as discriminating: `keysOf` writes both a null key and an empty
    // string as '', and says nothing about length, so a null-keyed group would
    // collide with an empty-string-keyed one — and both with the whole table.
    const k = JSON.stringify(prefix.map((v) => v.key));
    const hit = underCache.get(k);
    if (hit) return hit;
    const out = rowKeys.filter((rk) => prefix.every((v, i) => rk[i]?.key === v.key));
    underCache.set(k, out);
    return out;
  }

  function distinctTuples(rows: readonly ResultRow[], dims: readonly string[]): DimTuple[] {
    const seen = new Map<string, DimTuple>();
    for (const row of rows) {
      const tuple: DimValue[] = dims.map((d) => row.dims[d] ?? { key: null, label: null });
      const k = keysOf(tuple);
      if (!seen.has(k)) seen.set(k, tuple);
    }
    return [...seen.values()];
  }

  /**
   * Sum of one measure at EVERY tuple prefix, in one pass over the result.
   *
   * Prefixes rather than whole tuples because sorting happens level by level:
   * see {@link orderTuples}. One pass rather than a scan per prefix because the
   * alternative is quadratic in the result size — at the 5000-row cap that is
   * tens of millions of row visits, synchronously, on every spec edit.
   */
  function prefixSums(
    dims: readonly string[],
    measureId: string,
    at?: readonly string[],
  ): Map<string, number> {
    const sums = new Map<string, number>();
    for (const row of result.rows) {
      // `at` narrows the ranking to ONE column, so clicking March's header
      // orders by March rather than by the measure across every month.
      if (at && !at.every((key, i) => (row.dims[spec.cols[i] ?? '']?.key ?? '') === key)) continue;
      const v = row.values[measureId];
      // A null contributes nothing AND does not make the prefix "seen", so a
      // group with no values at all stays absent rather than summing to zero.
      if (v === null || v === undefined) continue;
      let key = '';
      for (let n = 0; n < dims.length; n++) {
        key =
          n === 0
            ? (row.dims[dims[n] ?? '']?.key ?? '')
            : `${key}${SEP}${row.dims[dims[n] ?? '']?.key ?? ''}`;
        sums.set(key, (sums.get(key) ?? 0) + v);
      }
    }
    return sums;
  }

  /**
   * Order the tuples of one axis.
   *
   * Comparison walks DOWN THE LEVELS and stops at the first that differs, which
   * keeps siblings under their parent. That nesting property is not cosmetic:
   * the nested column header widens a cell only while consecutive columns share
   * an ancestor path, so a flat sort that interleaved them would emit the same
   * group header twice and stop lining up with the cells below.
   *
   * WHICH ENTRIES DECIDE A GIVEN LEVEL depends on whether the spec names one.
   * An entry carrying `on` governs that dimension's level and no other, which
   * is how a per-field order is said — customers ranked by hours, employees
   * alphabetical inside them. A level that no entry names falls back to the
   * entries carrying no `on` at all: the original whole-axis chain, applied
   * identically at every level, which is what every report saved before `on`
   * existed still means. A spec with no `on` anywhere therefore orders exactly
   * as it did before this existed, at every level, by construction.
   *
   * With nothing to decide a level the tuples keep the order the server sent
   * them in — first appearance — so an `ORDER BY` upstream is respected rather
   * than overridden, and that same order breaks ties under a value sort.
   */
  function orderTuples(
    tuples: DimTuple[],
    dims: readonly string[],
    sorts: readonly SortSpec[],
  ): DimTuple[] {
    const firstSeen = new Map<string, number>();
    tuples.forEach((tuple, i) => {
      for (let n = 1; n <= tuple.length; n++) {
        const k = keysOf(tuple.slice(0, n));
        if (!firstSeen.has(k)) firstSeen.set(k, i);
      }
    });
    const seenAt = (prefix: DimTuple) => firstSeen.get(keysOf(prefix)) ?? 0;

    // One table per entry in the chain, computed once for the whole axis
    // rather than once per comparison. `at` only means anything when ranking
    // the ROW axis by one of the columns.
    const sums = sorts.map((sort) =>
      isLevelSort(sort.by)
        ? undefined
        : prefixSums(dims, sort.by, dims === spec.rows ? sort.at : undefined),
    );

    /**
     * The entries that decide level `i`, as indices into `sorts`.
     *
     * Indices rather than the entries themselves so `sums[n]` stays in step —
     * the prefix tables are built once per ENTRY, and re-deriving which table
     * belongs to which entry inside the comparator would be a lookup per
     * comparison. Resolved once per level for the same reason: the comparator
     * runs O(n log n) times over an axis that can be five thousand long.
     */
    const chainFor = dims.map((_, i) => {
      const level = sorts.flatMap((s, n) => (s.on === dims[i] ? [n] : []));
      return level.length > 0 ? level : sorts.flatMap((s, n) => (s.on === undefined ? [n] : []));
    });

    return [...tuples].sort((a, b) => {
      for (let i = 0; i < a.length; i++) {
        const av = a[i];
        const bv = b[i];
        if (av?.key === bv?.key) continue;

        const left = a.slice(0, i + 1);
        const right = b.slice(0, i + 1);
        // Whatever governs THIS level decides, in order: the first entry that
        // separates them wins, and the rest are its tie-breakers. For a
        // per-level order that is one entry; for the whole-axis chain it is
        // every entry, which is what it was before levels could be named.
        for (const n of chainFor[i] ?? []) {
          const sort = sorts[n];
          if (!sort) continue;
          const dir = sort.dir === 'desc' ? -1 : 1;
          if (sort.by === 'label') {
            const byLabel = LABEL_COLLATOR.compare(dimText(av), dimText(bv));
            if (byLabel !== 0) return dir * byLabel;
            continue;
          }
          if (sort.by === 'key') {
            // ONE comparator for both directions, so descending is exactly
            // ascending reversed. It cannot be the database's own order: that
            // is the server's collation (glibc `en_US.utf8` puts `"a b"`
            // before `"ab"` and `_x` last), which no JS collator reproduces.
            // What matters is that the two directions mirror; on the keys a
            // reader reverses — ISO dates, `YYYY-MM` — every collation agrees.
            //
            // An absent key goes LAST either way, as `NULLS LAST` puts it in
            // the statement: a "No engagement" group belongs at the bottom
            // whichever way the dates run. The level loop has already skipped
            // equal keys, so at most one of the two is absent here.
            const x = av?.key ?? null;
            const y = bv?.key ?? null;
            if (x === null) return 1;
            if (y === null) return -1;
            const byKey = KEY_COLLATOR.compare(x, y);
            if (byKey !== 0) return dir * byKey;
            continue;
          }
          // Absent sorts as the smallest thing there is.
          const table = sums[n];
          const x = table?.get(keysOf(left)) ?? Number.NEGATIVE_INFINITY;
          const y = table?.get(keysOf(right)) ?? Number.NEGATIVE_INFINITY;
          if (x !== y) return dir * (x - y);
        }
        // Nothing separated them, so keep the order the server sent — the
        // result never reshuffles between renders.
        return seenAt(left) - seenAt(right);
      }
      return 0;
    });
  }
}

/**
 * Where a total sits, which decides whether it means anything.
 *
 * `corner` is a subtotal band's rightmost cell: a total in BOTH directions at
 * once, so it needs both to hold. Distinct from `grand`, whose percentage
 * corner is 100% by construction rather than a sum of anything.
 */
export type TotalPosition = 'rowTotal' | 'colTotal' | 'grand' | 'corner';

/**
 * The two measures a ratio's total must be rebuilt from, or `undefined`.
 *
 * A ratio has a total; it just is not a sum. `(Over)/Under %` over a folded
 * customer is that customer's total variance over their total allowance — one
 * division at the level being printed — and never the sum, or the average, of
 * the percentages underneath. This says when that rebuild is available, and it
 * is the single gate: {@link totalIsMeaningful} decides whether to print a
 * cell from it, and `pivot()` computes the cell from it, so the two cannot
 * disagree about which measures have a total.
 *
 * All four conditions are refusals to publish a number nobody can defend:
 *
 *  - NOT A RATIO: nothing to rebuild. Additive measures total by summing.
 *  - HALVES NOT NAMED. A ratio may write its numerator out as SQL instead of
 *    naming a measure (see {@link Measure.numerator}); the browser has no SQL
 *    and no column to sum, so such a ratio keeps its em dash.
 *  - A HALF THAT DOES NOT ADD UP. The rebuild sums each half over the span,
 *    so a numerator that is itself an average or a distinct count gives a
 *    quotient of two numbers that mean nothing added together.
 *  - SHOWN AS A SHARE. Under `showAs` the printed cells are shares of an axis
 *    total, and a share of a share is not a quantity — which is why
 *    `canShowAs` refuses the combination outright. Checked here too, because a
 *    saved spec written before that guard existed is still a spec that runs.
 */
export function ratioParts(
  model: ReportModel,
  spec: ReportSpec,
  measureId: string,
): { numerator: string; denominator: string } | undefined {
  // The `showAs` half of the question, and the ONLY part that is about the
  // spec: under a percentage transform a total must not be rebuilt, because it
  // would be a share of a share. The shape half is `ratioHalves`.
  if (showAsFor(spec, measureId) !== 'raw') return undefined;
  return ratioHalves(model, measureId);
}

/**
 * The two additive measures a ratio divides, if it names a recoverable pair.
 *
 * {@link ratioParts} minus the `showAs` guard, for the caller that needs the
 * RAW value rather than a total: `showAs` is applied downstream of `raw()`, so
 * a cell rebuilt here is transformed afterwards exactly like any other.
 */
export function ratioHalves(
  model: ReportModel,
  measureId: string,
): { numerator: string; denominator: string } | undefined {
  const measure = findMeasure(model, measureId);
  if (!measure || measure.agg !== 'ratio') return undefined;
  if (!measure.numerator || !measure.denominator) return undefined;
  const numerator = findMeasure(model, measure.numerator);
  const denominator = findMeasure(model, measure.denominator);
  if (!numerator || !denominator) return undefined;
  if (!isAdditive(numerator) || !isAdditive(denominator)) return undefined;
  return { numerator: numerator.id, denominator: denominator.id };
}

/**
 * Whether a total is meaningful, and why not when it isn't.
 *
 * Two rules, both about not printing a confident wrong number:
 *
 *  - A NON-ADDITIVE measure never totals. The total of an average or an
 *    extremum is not the sum of its parts, and cannot be recovered from the
 *    displayed cells. A RATIO is the one kind that can be — not by summing,
 *    but by dividing its two halves' totals — so a ratio that names both of
 *    them totals anyway. See {@link ratioParts}.
 *  - A PERCENTAGE only totals along the axis it was computed over. Under
 *    "% of row", the bottom total would be adding shares of different rows,
 *    which is not a quantity.
 */
export function totalIsMeaningful(
  model: ReportModel,
  spec: ReportSpec,
  measureId: string,
  where: TotalPosition,
): boolean {
  const measure: Measure | undefined = findMeasure(model, measureId);
  if (!measure) return false;
  // A ratio that can be REBUILT is the exception to the first rule, not a hole
  // in it: nothing is being summed. Its total is its numerator's total over its
  // denominator's, computed once at this level — which is the same arithmetic
  // the server did for the cells below, over a wider span.
  if (ratioParts(model, spec, measureId)) return true;
  if (!isAdditive(measure)) return false;

  if (where === 'corner') {
    return (
      totalIsMeaningful(model, spec, measureId, 'rowTotal') &&
      totalIsMeaningful(model, spec, measureId, 'colTotal')
    );
  }

  const mode = showAsFor(spec, measureId);
  if (mode === 'raw' || where === 'grand') return true;
  if (mode === 'pctRow') return where === 'rowTotal';
  if (mode === 'pctCol') return where === 'colTotal';
  return true; // pctTotal: both axes are shares of the same whole
}
